import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useRouteError } from 'react-router';

import log from 'loglevel';

import { APP_PAGES } from './constants';
import {
  isStaleChunkError,
  recoverFromStaleChunks,
} from '../utils/staleChunkRecovery';

const stylesheet = {
  container:
    'flex flex-col items-center justify-center min-h-screen bg-semantic-bg-page p-4',
  title: 'text-xl font-semibold text-semantic-text-primary mb-2',
  description: 'text-sm text-semantic-text-secondary mb-4 text-center',
  actions: 'flex items-center gap-3',
  button:
    'px-4 py-2 bg-semantic-bg-accent text-semantic-emerald-fg rounded-semantic hover:bg-semantic-bg-accent-hover transition-colors',
  link: 'px-4 py-2 border border-semantic-border text-semantic-text-primary rounded-semantic hover:bg-semantic-bg-hover transition-colors',
};

const RETRY_STORAGE_KEY = 'route_error_retry';
const AUTO_RETRY_DELAY_MS = 500;
const RETRY_EXPIRY_MS = 10000;

function shouldAutoRetry(): boolean {
  try {
    const stored = sessionStorage.getItem(RETRY_STORAGE_KEY);
    if (!stored) return true;

    const { timestamp } = JSON.parse(stored);
    const elapsed = Date.now() - timestamp;

    if (elapsed > RETRY_EXPIRY_MS) return true;

    return false;
  } catch {
    return true;
  }
}

function markRetryAttempt(): void {
  try {
    sessionStorage.setItem(
      RETRY_STORAGE_KEY,
      JSON.stringify({ timestamp: Date.now() }),
    );
  } catch {
    // sessionStorage not available
  }
}

export function RouteErrorBoundary() {
  const error = useRouteError();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasTriedAutoRetry = useRef(false);

  useEffect(() => {
    log.error('Route error caught:', error);

    if (isStaleChunkError(error) && recoverFromStaleChunks()) {
      hasTriedAutoRetry.current = true;
      return;
    }

    if (!hasTriedAutoRetry.current && shouldAutoRetry()) {
      hasTriedAutoRetry.current = true;
      markRetryAttempt();
      const timer = setTimeout(() => {
        navigate(0);
      }, AUTO_RETRY_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [error, navigate]);

  if (!hasTriedAutoRetry.current && shouldAutoRetry()) {
    return null;
  }

  // Re-mark instead of clearing: clearing re-arms the auto-retry, so a failing
  // route reloads twice and flashes a blank screen before showing this again.
  const handleManualRetry = () => {
    markRetryAttempt();
    navigate(0);
  };

  return (
    <div className={stylesheet.container}>
      <p className={stylesheet.title}>{t('errors.unexpectedError.title')}</p>
      <p className={stylesheet.description}>
        {t('errors.unexpectedError.description')}
      </p>
      <div className={stylesheet.actions}>
        <button
          type="button"
          className={stylesheet.button}
          onClick={handleManualRetry}
        >
          {t('errors.unexpectedError.retry')}
        </button>
        <a className={stylesheet.link} href={APP_PAGES.DOBOX}>
          {t('errors.notFound.backToDoBox')}
        </a>
      </div>
    </div>
  );
}
